<script setup lang="ts">
import { computed, ref, useTemplateRef, watch } from "vue";

import BaseTextarea from "./BaseTextarea.vue";

defineOptions({ inheritAttrs: false });

const model = defineModel<string>({ required: true });

const props = withDefaults(
  defineProps<{
    autofocus?: boolean;
    summaryMaxLength?: number;
    expandDescription?: boolean;
    readonly?: boolean;
  }>(),
  { summaryMaxLength: 90 },
);

const expanded = ref(false);
const textareaRef = useTemplateRef("textarea");
const summaryRef = useTemplateRef("summary");

watch(
  () => props.expandDescription,
  (expand) => (expanded.value = expand),
  { immediate: true },
);

const shouldShowSummary = computed(
  () =>
    model.value.trim() !== "" && !expanded.value && (!props.expandDescription || showButtons.value),
);
// An empty (or whitespace-only) description collapses to a content-hugging placeholder (click to
// edit) rather than a full-width textarea; the textarea only takes over while
// actively editing.
const showPlaceholder = computed(
  () => !props.readonly && model.value.trim() === "" && !expanded.value,
);
const summaryText = computed(() => model.value.split("\n")[0]);
const showButtons = computed(() => {
  const totalLength = model.value.length;
  const hasNewLines = model.value.includes("\n");
  return (totalLength > props.summaryMaxLength || hasNewLines) && props.summaryMaxLength > 0;
});

// Set only by a user-initiated toggle so onAfterEnter doesn't steal focus on
// initial render or a global expand/collapse.
let pendingFocus = false;

function toggleExpand() {
  expanded.value = !expanded.value;
  pendingFocus = true;
}

// Focus the element that just entered — textarea when expanding, summary when
// collapsing. Driven by the transition's completion, so it's instant for the
// no-fade swap and lands right after the fade otherwise (no fixed timer).
function onAfterEnter() {
  if (!pendingFocus) return;
  pendingFocus = false;
  (expanded.value ? textareaRef : summaryRef).value?.focus();
}

function onFocus() {
  expanded.value = true;
}

// On blur, revert to the read view (placeholder when empty, hugging summary
// otherwise) unless the text is long enough to warrant a "see more" toggle —
// those stay expanded and collapse via "see less". `showButtons` is already
// false for empty text. Kept expanded when descriptions are globally expanded.
function onBlur() {
  if (!showButtons.value) {
    expanded.value = props.expandDescription ?? false;
  }
}
</script>

<template>
  <div>
    <!-- Fade only applies to the see-more expand/collapse; short/empty
    descriptions use a no-op (no CSS) transition so the read↔edit swap is
    instant. Keep `css` on — with `mode="out-in"` a css-less transition has no
    completion signal, so the incoming element would never mount. -->
    <Transition
      :name="showButtons ? 'faster-fade' : 'no-fade'"
      mode="out-in"
      @after-enter="onAfterEnter"
    >
      <!-- Read Mode (before user interaction) -->
      <button
        v-if="shouldShowSummary"
        ref="summary"
        class="description-summary"
        :aria-label="$t('label.objective.summaryButton', { summary: summaryText })"
        @click="toggleExpand"
      >
        <span class="description-summary-text">{{ summaryText }}</span>
        <span v-if="showButtons" class="see-more-link">
          {{ $t("textarea.seeMore") }}
        </span>
      </button>
      <!-- Placeholder -->
      <button
        v-else-if="showPlaceholder"
        class="description-summary description-placeholder"
        @click="toggleExpand"
      >
        {{ $t("objectives.description.placeholder") }}
      </button>
      <!-- Edit Mode -->
      <div v-else>
        <BaseTextarea
          ref="textarea"
          v-bind="$attrs"
          v-model="model"
          class="description-textarea"
          :readonly
          :placeholder="$t('objectives.description.placeholder')"
          :class="{ expanded }"
          @focus="onFocus"
          @blur="onBlur"
        />
        <button v-if="showButtons" class="see-less-link" @click="toggleExpand">
          <span>{{ $t("textarea.seeLess") }}</span>
        </button>
      </div>
    </Transition>
  </div>
</template>

<style scoped lang="scss">
@use "@/styles/variables/colors";
@use "@/styles/mixins/utils";
@use "@/styles/mixins/typography";

// `inline-flex` + no `flex: 1` on the text makes the button hug its content,
// so only the visible summary text is clickable (not the row's trailing
// whitespace). `cursor: text` signals the underlying description is editable.
.description-summary {
  padding: 0.5rem;
  color: colors.$text;
  display: inline-flex;
  align-items: center;
  max-width: 100%;
  outline: revert;
  cursor: text;

  // Borders to align the component with the textarea (making the swap seamless between the two)
  border: 1px solid transparent;

  &-text {
    @include utils.ellipsis;

    // A textarea preserves a leading space, but `nowrap` (from the ellipsis
    // mixin) collapses it — which shifted the text a couple pixels on swap.
    // `pre` keeps the space while still truncating with an ellipsis.
    white-space: pre;
    min-width: 0;
  }
}

.description-placeholder {
  color: colors.$text-placeholder;
}

.description-textarea {
  display: block; // Default of inline-block causes a block parent to add a variable amount of space below the textarea
}

.see-more-link,
.see-less-link {
  @include typography.medium("semi-bold");

  flex-shrink: 0;
  white-space: nowrap;
  cursor: pointer;
  color: colors.$text-subtle;
  outline: revert;
  padding-left: 0.5rem;
  text-decoration: underline;
  text-underline-offset: 15%;
}
</style>
